import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { I18n } from '@fiverr-private/i18n-react';
import pathfinder from '@fiverr-private/pathfinder';
import { logger } from '@fiverr-private/obs';
import {
    registerFloatingChatListeners,
    unregisterFloatingChatListeners,
} from '@fiverr-private/go_floating_chat_triggers';
import { EducationTooltip, EducationTooltipAnchor, EducationTooltipContent } from '@fiverr-private/education_tooltip';
import { sendBigQueryEvent } from '../../utils/bigQuery';
import { sendMixPanelEvent } from '../../utils/mixPanel';
import {
    BIGQUERY_EVENT_ACKNOWLEDGED,
    BIGQUERY_EVENT_IMPRESSION,
    MIXPANEL_EVENT_ACKNOWLEDGED,
    MIXPANEL_EVENT_IMPRESSION,
} from './constants';

type TooltipStatus = 'closed' | 'open' | 'acknowledged';

interface Payload {
    data?: {
        sellerName: string;
    };
}

export const ChatTooltipWrapper = ({ children }: { children: React.ReactNode }) => {
    const [tooltipStatus, setTooltipStatus] = useState<TooltipStatus>('closed');
    const [sellerName, setSellerName] = useState<string>('');

    useEffect(() => {
        if (tooltipStatus !== 'acknowledged') {
            const callback = (payload?: Payload) => {
                setTooltipStatus('open');
                sendMixPanelEvent(MIXPANEL_EVENT_IMPRESSION);
                sendBigQueryEvent(BIGQUERY_EVENT_IMPRESSION);
                if (payload?.data?.sellerName) {
                    setSellerName(payload?.data?.sellerName);
                }
            };
            registerFloatingChatListeners({ GO_OPEN_FLOATING_CHAT_TOOLTIP: callback });
            return () => {
                unregisterFloatingChatListeners({ GO_OPEN_FLOATING_CHAT_TOOLTIP: callback });
            };
        }
    }, [tooltipStatus]);

    const handleOpenChanged = async (isOpen: boolean) => {
        if (!isOpen) {
            setTooltipStatus('acknowledged');
            try {
                await axios.post(pathfinder('personal_assistant_post_chat_tooltip_ack'));
                sendMixPanelEvent(MIXPANEL_EVENT_ACKNOWLEDGED);
                sendBigQueryEvent(BIGQUERY_EVENT_ACKNOWLEDGED);
            } catch (error) {
                logger.error(error as Error, {
                    description: 'Cant acknowledge chat tooltip',
                    flow: 'ChatTooltipWrapper',
                });
            }
        }
    };

    if (tooltipStatus === 'closed' || tooltipStatus === 'acknowledged') {
        return <>{children}</>;
    }

    return (
        <EducationTooltip onOpenChanged={handleOpenChanged} position="bottom">
            <EducationTooltipAnchor>{children}</EducationTooltipAnchor>
            <EducationTooltipContent closeOnClickOutside={false}>
                <I18n k="header.chat_tooltip.title" params={{ sellerName }} />
            </EducationTooltipContent>
        </EducationTooltip>
    );
};
